/**
 * Calendar and contacts, on top of the same JMAP session as mail.
 *
 * Stalwart v0.15.5 advertises urn:ietf:params:jmap:calendars and :contacts and
 * serves Calendar/get, CalendarEvent/get|set|query, AddressBook/get and
 * ContactCard/get|set — all verified against the live server before this was
 * written, because "the capability is advertised" and "the method answers" are
 * not the same claim.
 *
 * Events are JSCalendar (RFC 8984) and cards are JSContact (RFC 9553). Both use
 * keyed maps rather than arrays for multi-valued fields, which is why reading a
 * contact's email means taking Object.values() rather than [0].
 */
import { CORE, JmapClient } from "./jmap";

export const CALENDARS = "urn:ietf:params:jmap:calendars";
export const CONTACTS = "urn:ietf:params:jmap:contacts";

export interface Calendar {
  id: string;
  name: string;
  color: string | null;
  timeZone: string | null;
  isDefault: boolean;
}

/**
 * A JSCalendar event.
 *
 * `start` is a *local* date-time with no zone suffix ("2026-07-23T10:00:00")
 * and the zone is carried separately in `timeZone` — so it must not be handed
 * to `new Date()` expecting UTC.
 */
export interface CalendarEvent {
  id: string;
  title: string | null;
  description: string | null;
  start: string;
  duration: string | null;
  timeZone: string | null;
  showWithoutTime?: boolean;
  calendarIds: Record<string, boolean>;
}

export interface AddressBook {
  id: string;
  name: string;
}

export interface ContactCard {
  id: string;
  name?: { full?: string } | null;
  emails?: Record<string, { address?: string }> | null;
  phones?: Record<string, { number?: string }> | null;
  organizations?: Record<string, { name?: string }> | null;
  titles?: Record<string, { name?: string }> | null;
}

/** ISO 8601 duration → minutes. Handles the PnDTnHnMnS subset events use. */
export function durationMinutes(duration: string | null | undefined): number {
  if (!duration) return 0;
  const m = duration.match(/^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/);
  if (!m) return 0;
  const [, d, h, min, s] = m;
  return (+(d ?? 0) * 1440) + (+(h ?? 0) * 60) + +(min ?? 0) + Math.round(+(s ?? 0) / 60);
}

/** "2026-07-23T10:00:00" → "2026-07-23", without going through Date. */
export function localDay(start: string): string {
  return start.slice(0, 10);
}

/** "2026-07-23T10:00:00" → "10:00". */
export function localTime(start: string): string {
  return start.slice(11, 16);
}

export class CalendarClient {
  constructor(private jmap: JmapClient) {}

  async calendars(): Promise<Calendar[]> {
    const accountId = await this.jmap.account();
    const [result] = await this.jmap.request<{ list: Calendar[] }>(
      [["Calendar/get", { accountId, ids: null }, "0"]],
      [CORE, CALENDARS],
    );
    return result.list;
  }

  /**
   * Events overlapping a window.
   *
   * CalendarEvent/query takes UTCDate bounds, so the caller passes real Dates
   * and the local-time fields on the results are formatted for display later.
   */
  async events(after: Date, before: Date): Promise<CalendarEvent[]> {
    const accountId = await this.jmap.account();
    const responses = await this.jmap.request<unknown>(
      [
        [
          "CalendarEvent/query",
          {
            accountId,
            filter: { after: after.toISOString(), before: before.toISOString() },
          },
          "0",
        ],
        [
          "CalendarEvent/get",
          {
            accountId,
            "#ids": { resultOf: "0", name: "CalendarEvent/query", path: "/ids" },
            properties: ["id", "title", "description", "start", "duration", "timeZone", "showWithoutTime", "calendarIds"],
          },
          "1",
        ],
      ],
      [CORE, CALENDARS],
    );
    return (responses[1] as { list: CalendarEvent[] }).list;
  }

  async createEvent(event: {
    calendarId: string;
    title: string;
    start: string;
    duration: string;
    timeZone: string;
    description?: string;
  }): Promise<string> {
    const accountId = await this.jmap.account();
    const [result] = await this.jmap.request<{ created?: Record<string, { id: string }> }>(
      [
        [
          "CalendarEvent/set",
          {
            accountId,
            create: {
              e: {
                "@type": "Event",
                calendarIds: { [event.calendarId]: true },
                title: event.title,
                start: event.start,
                duration: event.duration,
                timeZone: event.timeZone,
                ...(event.description ? { description: event.description } : {}),
              },
            },
          },
          "0",
        ],
      ],
      [CORE, CALENDARS],
    );
    const id = result.created?.e?.id;
    if (!id) throw new Error("The server did not create the event");
    return id;
  }

  async deleteEvent(id: string): Promise<void> {
    const accountId = await this.jmap.account();
    await this.jmap.request([["CalendarEvent/set", { accountId, destroy: [id] }, "0"]], [CORE, CALENDARS]);
  }

  async addressBooks(): Promise<AddressBook[]> {
    const accountId = await this.jmap.account();
    const [result] = await this.jmap.request<{ list: AddressBook[] }>(
      [["AddressBook/get", { accountId, ids: null }, "0"]],
      [CORE, CONTACTS],
    );
    return result.list;
  }

  async contacts(): Promise<ContactCard[]> {
    const accountId = await this.jmap.account();
    const [result] = await this.jmap.request<{ list: ContactCard[] }>(
      [["ContactCard/get", { accountId, ids: null }, "0"]],
      [CORE, CONTACTS],
    );
    return [...result.list].sort((a, b) =>
      (a.name?.full ?? "").localeCompare(b.name?.full ?? ""),
    );
  }

  async createContact(card: {
    addressBookId: string;
    fullName: string;
    email?: string;
    phone?: string;
    organization?: string;
    title?: string;
  }): Promise<string> {
    const accountId = await this.jmap.account();
    const [result] = await this.jmap.request<{ created?: Record<string, { id: string }> }>(
      [
        [
          "ContactCard/set",
          {
            accountId,
            create: {
              c: {
                "@type": "Card",
                addressBookIds: { [card.addressBookId]: true },
                name: { full: card.fullName },
                ...(card.email ? { emails: { e1: { address: card.email } } } : {}),
                ...(card.phone ? { phones: { p1: { number: card.phone } } } : {}),
                ...(card.organization ? { organizations: { o1: { name: card.organization } } } : {}),
                ...(card.title ? { titles: { t1: { name: card.title } } } : {}),
              },
            },
          },
          "0",
        ],
      ],
      [CORE, CONTACTS],
    );
    const id = result.created?.c?.id;
    if (!id) throw new Error("The server did not create the contact");
    return id;
  }

  async deleteContact(id: string): Promise<void> {
    const accountId = await this.jmap.account();
    await this.jmap.request([["ContactCard/set", { accountId, destroy: [id] }, "0"]], [CORE, CONTACTS]);
  }
}

/** First value of a JSContact keyed map — those fields are maps, not arrays. */
export function firstValue<T>(map: Record<string, T> | null | undefined): T | undefined {
  return map ? Object.values(map)[0] : undefined;
}

export function contactEmail(card: ContactCard): string | undefined {
  return firstValue(card.emails)?.address;
}

export function contactName(card: ContactCard): string {
  return card.name?.full ?? contactEmail(card) ?? "Unnamed";
}
